import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { toast } from 'react-toastify';
import { assetDeepLink } from '../utils/assetLinks';

/**
 * Per-asset QR labels (requirement 8.3).
 *
 * Each label's QR carries the asset's absolute deep link, so any phone camera opens the
 * asset page directly, and the workspace's own scanner (Scan asset) resolves it too.
 *
 *   <AssetQrLabel asset={{ kind, id, ref, name, location }} />    one label, on screen + print
 *   printAssetLabels(assets, { heading })                         a batch sheet (A4, 3 across)
 */

const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const qrFor = (a, width = 220) => QRCode.toDataURL(assetDeepLink(a.kind, a.id), { margin: 1, width, errorCorrectionLevel: 'M' });

const SHEET_CSS = `
  @page { size: A4; margin: 10mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, 'Segoe UI', Roboto, Arial, sans-serif; color: #111827; margin: 0; }
  h1 { font-size: 13px; font-weight: 600; margin: 0 0 6mm; color: #4b5563; }
  .sheet { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4mm; }
  .label { border: 0.3mm dashed #9ca3af; border-radius: 2mm; padding: 3mm; display: flex; gap: 3mm; align-items: center; break-inside: avoid; page-break-inside: avoid; min-height: 34mm; }
  .label img { width: 26mm; height: 26mm; flex: none; image-rendering: pixelated; }
  .ref { font-family: ui-monospace, Menlo, monospace; font-weight: 700; font-size: 11px; word-break: break-all; }
  .name { font-size: 10px; margin-top: 1mm; }
  .meta { font-size: 8.5px; color: #6b7280; margin-top: 1mm; }
  .scan { font-size: 7.5px; color: #6b7280; margin-top: 1.5mm; text-transform: uppercase; letter-spacing: 0.04em; }
  @media screen { body { padding: 12mm; background: #f3f4f6; } .sheet { background: #fff; padding: 6mm; } }
`;

/** Open a print-ready sheet of QR labels for `assets` ([{ kind, id, ref, name, location, property_name }]). */
export async function printAssetLabels(assets, { heading = 'Asset labels' } = {}) {
  const list = (assets || []).filter((a) => a && a.kind && a.id);
  if (!list.length) { toast.info('Choose at least one asset to label'); return; }
  // Open the window inside the click so pop-up blockers allow it, then fill it.
  const win = window.open('', '_blank');
  if (!win) { toast.error('Your browser blocked the label sheet — allow pop-ups for CloudSafe and try again'); return; }
  win.document.write('<!doctype html><title>Preparing labels…</title><p style="font-family:sans-serif">Preparing labels…</p>');
  try {
    const images = await Promise.all(list.map((a) => qrFor(a, 240)));
    const cells = list.map((a, i) => `
      <div class="label">
        <img src="${images[i]}" alt="QR code for ${esc(a.ref || a.name)}">
        <div>
          <div class="ref">${esc(a.ref || `${a.kind}-${a.id}`)}</div>
          ${a.name && a.name !== a.ref ? `<div class="name">${esc(a.name)}</div>` : ''}
          ${a.location || a.property_name ? `<div class="meta">${esc([a.property_name, a.location].filter(Boolean).join(' · '))}</div>` : ''}
          <div class="scan">Scan to open in CloudSafe</div>
        </div>
      </div>`).join('');
    win.document.open();
    win.document.write(`<!doctype html><html lang="en"><head><meta charset="utf-8"><title>${esc(heading)}</title><style>${SHEET_CSS}</style></head>
      <body><h1>${esc(heading)} · ${list.length} label${list.length === 1 ? '' : 's'}</h1><div class="sheet">${cells}</div>
      <script>window.addEventListener('load', function () { setTimeout(function () { window.print(); }, 150); });</script></body></html>`);
    win.document.close();
  } catch (e) {
    win.close();
    toast.error('Couldn’t make the QR labels — try again');
  }
}

/** One asset's QR label, shown on its page, with print + copy-link actions. */
export const AssetQrLabel = ({ asset }) => {
  const [src, setSrc] = useState(null);
  const link = asset ? assetDeepLink(asset.kind, asset.id) : '';
  useEffect(() => {
    let live = true;
    if (asset) qrFor(asset, 320).then((d) => { if (live) setSrc(d); }).catch(() => { if (live) setSrc(null); });
    return () => { live = false; };
  }, [asset]);
  if (!asset) return null;
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); toast.success('Link copied'); }
    catch { toast.error('Couldn’t copy — select the link and copy it instead'); }
  };
  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
      <div style={{ border: '1.5px dashed var(--cw-border)', borderRadius: 10, padding: 10, background: '#fff', lineHeight: 0 }}>
        {src ? <img src={src} alt={`QR code that opens ${asset.ref || asset.name}`} width={140} height={140} style={{ imageRendering: 'pixelated' }} />
          : <div style={{ width: 140, height: 140 }} aria-hidden="true" />}
      </div>
      <div style={{ flex: '1 1 220px', minWidth: 0 }}>
        <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 700 }}>{asset.ref}</div>
        <div className="cw-meta" style={{ marginTop: 4, wordBreak: 'break-all' }}>{link}</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          <button type="button" className="cw-btn cw-btn-primary" onClick={() => printAssetLabels([asset], { heading: asset.property_name || 'Asset label' })}>
            <i className="fa-solid fa-print" aria-hidden="true" /> Print label
          </button>
          <button type="button" className="cw-btn cw-btn-secondary" onClick={copy}><i className="fa-regular fa-copy" aria-hidden="true" /> Copy link</button>
        </div>
      </div>
    </div>
  );
};

export default AssetQrLabel;
